import { useCallback, useEffect, useState } from 'react';
import { logError } from 'src/services/LoggingService';
import { getQuoteActivities } from 'src/services/QuoteService';

/**
 * Lazy, cursor-paginated activity fetcher for the latest-activity dropdown tooltips
 * (Radar table row, Radar map card, Orders row). Keeps pagination state in the caller so
 * ActivityDropdownList stays presentational, instead of duplicating fetch/load-more logic
 * across every usage.
 *
 * Ported from wal-frontend-v2 src/hooks/useActivityList.ts. The portal has a single activity
 * endpoint (getQuoteActivities, which serves both 'order' and 'quote' entity types), so the
 * two-service branch and its response-shape normalization are dropped — everything here goes
 * through the one endpoint. Activity-type filtering is applied server-side by that shared
 * endpoint, so there is nothing to filter client-side.
 *
 * The initial fetch fires only when `enabled` flips true (the tooltip opens) and resets when the
 * entity changes, so each tooltip starts from page one.
 */

// Cursor-based pagination shape returned by the activity endpoint.
interface IActivityPagination {
    hasMore?: boolean;
    nextCursor?: number | null;
}

interface IUseActivityListParams {
    entityType: 'order' | 'quote';
    entityId?: number | string | null;
    // Gate the initial fetch — the latest-activity tooltips fetch lazily, only when opened.
    enabled: boolean;
    // Page size — matches the WAL activity feed (25).
    limit?: number;
}

interface IUseActivityListResult {
    activities: any[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    loadMore: () => void;
}

const useActivityList = ({ entityType, entityId, enabled, limit = 25 }: IUseActivityListParams): IUseActivityListResult => {
    const [activities, setActivities] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pagination, setPagination] = useState<IActivityPagination | null>(null);

    const fetchActivities = useCallback(
        async ({ lastId, append }: { lastId?: number | null; append: boolean }) => {
            if (!entityId) return;

            if (append) setIsLoadingMore(true);
            else setIsLoading(true);

            try {
                const response: any = await getQuoteActivities({
                    entityType,
                    entityId: Number(entityId),
                    perPage: limit,
                    ...(lastId ? { lastId } : {}),
                });

                const list = response?.data?.activities || [];
                const nextPagination = response?.data?.pagination ?? null;

                // append -> keep accumulating older rows under the existing ones (infinite scroll);
                // !append -> replace, so re-opening / switching entity starts fresh from page one.
                setActivities((prev) => (append ? [...prev, ...list] : list));
                setPagination(nextPagination);
            } catch (err: any) {
                logError(err);
            } finally {
                if (append) setIsLoadingMore(false);
                else setIsLoading(false);
            }
        },
        [entityId, entityType, limit]
    );

    // Initial fetch when the tooltip opens (enabled flips true).
    useEffect(() => {
        if (!enabled || !entityId) return;

        // Reset to page one whenever we (re)open or the entity changes.
        setActivities([]);
        setPagination(null);

        fetchActivities({ append: false });
    }, [enabled, entityId, fetchActivities]);

    const loadMore = useCallback(() => {
        if (isLoading || isLoadingMore) return;
        if (!pagination?.hasMore || !pagination?.nextCursor) return;

        fetchActivities({ lastId: pagination.nextCursor, append: true });
    }, [isLoading, isLoadingMore, pagination, fetchActivities]);

    return {
        activities,
        isLoading,
        isLoadingMore,
        hasMore: !!pagination?.hasMore,
        loadMore,
    };
};

export default useActivityList;
